/**
 * 5 list pages для ПДВ-документів (Підпорція 3.1c, 2026-05-24).
 *
 * Уніфікований підхід — DataTable list-only pages. Editor flow для
 * minimum scope (advance + appendix_2) поки що через прямий API
 * (admin UI). Повний editor — TECHDEBT (UX-розширення).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  advancePayments,
  appendix1,
  appendix2,
  propRecalc,
  propCoef,
  type AdvancePayment,
  type Appendix1,
  type Appendix2,
  type PropRecalc,
  type PropCoef,
  type Appendix2Subtype,
} from '@/api/vatDocuments';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';


const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });


// ============================== Advance Payments ====================


export function AdvancePaymentsPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['advance-payments'],
    queryFn: () => advancePayments.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<AdvancePayment>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      { key: 'source_doc_type', header: 'Джерело', width: 130, sortValue: (r) => r.source_doc_type ?? '', cell: (r) => r.source_doc_type },
      {
        key: 'advance_amount', header: 'Сума', width: 140, align: 'right',
        sortValue: (r) => Number(r.advance_amount),
        cell: (r) => fmt2(r.advance_amount),
      },
      {
        key: 'vat_amount', header: 'ПДВ', width: 130, align: 'right',
        sortValue: (r) => Number(r.vat_amount),
        cell: (r) => fmt2(r.vat_amount),
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => r.posted ? 1 : 0,
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Авансові реєстрації ПДВ</h2>
      </div>
      <DataTable<AdvancePayment>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/taxation/advance-payment-registrations/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


// ============================== Appendix 1 ===========================


export function Appendix1Page() {
  const list = useQuery({
    queryKey: ['appendix-1'],
    queryFn: () => appendix1.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<Appendix1>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      { key: 'parent_tax_invoice_id', header: 'Parent ПН', sortValue: (r) => r.parent_tax_invoice_id ?? '', cell: (r) => r.parent_tax_invoice_id },
      {
        key: 'total_items_count', header: 'Кільк. позицій', width: 140, align: 'right',
        sortValue: (r) => r.total_items_count,
        cell: (r) => r.total_items_count,
      },
      {
        key: 'total_amount', header: 'Сума', width: 140, align: 'right',
        sortValue: (r) => Number(r.total_amount),
        cell: (r) => fmt2(r.total_amount),
      },
      {
        key: 'total_vat', header: 'ПДВ', width: 130, align: 'right',
        sortValue: (r) => Number(r.total_vat),
        cell: (r) => fmt2(r.total_vat),
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => r.posted ? 1 : 0,
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Додаток 1 до ПН (auto-generated)</h2>
        <small style={{ color: '#64748b' }}>
          Автоматично створюється для ПН з більш ніж 20 позиціями
        </small>
      </div>
      <DataTable<Appendix1>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


// ============================== Appendix 2 ===========================


export function Appendix2Page() {
  const [subtypeFilter, setSubtypeFilter] = useState<'ALL' | Appendix2Subtype>('ALL');
  const list = useQuery({
    queryKey: ['appendix-2', { subtypeFilter }],
    queryFn: () => appendix2.list({
      limit: 200,
      ...(subtypeFilter !== 'ALL' ? { operation_subtype: subtypeFilter } : {}),
    }),
  });
  const cols = useMemo<ColumnDef<Appendix2>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      { key: 'operation_subtype', header: 'Підтип', width: 200, sortValue: (r) => r.operation_subtype ?? '', cell: (r) => r.operation_subtype },
      { key: 'parent_tax_invoice_id', header: 'Parent ПН', sortValue: (r) => r.parent_tax_invoice_id ?? '', cell: (r) => r.parent_tax_invoice_id },
      {
        key: 'total_amount', header: 'Сума', width: 140, align: 'right',
        sortValue: (r) => Number(r.total_amount),
        cell: (r) => fmt2(r.total_amount),
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => r.posted ? 1 : 0,
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Додаток 2 до ПН (специфічні операції)</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label>Підтип:</label>
          <select
            value={subtypeFilter}
            onChange={(e) => setSubtypeFilter(e.target.value as 'ALL' | Appendix2Subtype)}
            data-testid="app2-subtype-filter"
            style={{ width: 140, flex: '0 0 auto' }}
          >
            <option value="ALL">Усі</option>
            <option value="WARRANTY_REPAIR">Гарантійний ремонт</option>
            <option value="CONSTRUCTION">Будівельні роботи</option>
            <option value="EQUIPMENT_INSTALLATION">Встановлення обладнання</option>
            <option value="OTHER">Інше</option>
          </select>
        </div>
      </div>
      <DataTable<Appendix2>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


// ============================== Proportional Recalc ==================


export function PropRecalcPage() {
  const list = useQuery({
    queryKey: ['prop-recalc'],
    queryFn: () => propRecalc.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<PropRecalc>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      { key: 'year', header: 'Рік', width: 100, sortValue: (r) => r.year, cell: (r) => r.year },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      {
        key: 'coefficient_actual', header: 'Факт. коеф.', width: 140, align: 'right',
        sortValue: (r) => Number(r.coefficient_actual),
        cell: (r) => r.coefficient_actual,
      },
      {
        key: 'coefficient_used_during_year', header: 'Викор. коеф.', width: 150, align: 'right',
        sortValue: (r) => Number(r.coefficient_used_during_year),
        cell: (r) => r.coefficient_used_during_year,
      },
      {
        key: 'difference_amount', header: 'Різниця', width: 140, align: 'right',
        sortValue: (r) => Number(r.difference_amount),
        cell: (r) => fmt2(r.difference_amount),
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => r.posted ? 1 : 0,
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Перерахунок пропорційного ПДВ (ст.199 ПКУ)</h2>
      </div>
      <DataTable<PropRecalc>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


// ============================== Proportional Coefficient ============


export function PropCoefPage() {
  const list = useQuery({
    queryKey: ['prop-coef'],
    queryFn: () => propCoef.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<PropCoef>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      { key: 'year', header: 'Рік', width: 100, sortValue: (r) => r.year, cell: (r) => r.year },
      { key: 'coefficient', header: 'Коефіцієнт', width: 140, sortValue: (r) => Number(r.coefficient), cell: (r) => r.coefficient },
      { key: 'based_on_year', header: 'На основі року', width: 150, sortValue: (r) => r.based_on_year, cell: (r) => r.based_on_year },
      { key: 'approval_date', header: 'Затверджено', width: 130, sortValue: (r) => r.approval_date ?? '', cell: (r) => r.approval_date },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => r.posted ? 1 : 0,
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Коефіцієнт пропорційного ПДВ</h2>
      </div>
      <DataTable<PropCoef>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
